import { ZipCodeStoreContextWrapper } from '@common/contexts';
import { FormInput, spreadAndtransformDefaultValues } from '@common/components/forms';
import { useApp, useQuery, useZipCodeChanged } from '@common/hooks';
import { IIntakeModel } from '@common/models';
import { Page } from '@customer/components/layout/.';
import { ZipCodeStore } from '@common/stores';
import { useCreateIntakeStore } from '@customer/stores/hooks';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTimeUtils } from '@common/utils/datetimeUtils';

ZipCodeStoreContextWrapper.set(new ZipCodeStore());

export const IntakeCustomerDetail = observer(() => {
    const store = useCreateIntakeStore({ shouldSetContext: false });
    const { configuration } = useApp();
    const { intakepublicid } = useParams();
    const query: URLSearchParams = useQuery();
    const navigate = useNavigate();

    const { register, handleSubmit, setValue, control, reset, watch, formState: { errors } } = useForm<IIntakeModel>({
        shouldFocusError: true,
        defaultValues: spreadAndtransformDefaultValues(store.intake as IIntakeModel)
    });

    useZipCodeChanged(watch, setValue, {
        zipCode: "zipCode",
		city: "city",
		county: "county",
		stateId: "stateId"
    }, false, false);

    useEffect(() => {
        if (intakepublicid) {
            store.setParamValues(intakepublicid, query.get('linkSource') || 'email');
            store.getIntake().then(() => {
                reset(spreadAndtransformDefaultValues(store.intake as IIntakeModel));
            }).catch(() => {
                navigate("/verification");
            });
        }
    }, [store, intakepublicid, query, reset, navigate]);

    const onSaveClick = useCallback((data: IIntakeModel) => {
        store.updateIntake(DateTimeUtils.objToDateTime(data)).then(() => {
            navigate(`/intake/${intakepublicid}`);
        });
    }, [store, navigate, intakepublicid]);

    return (
        <>
            {
                <Page>
                    <Page.Content>
                        <Row>
                            <Col xs={12}>
                                <h3>Your information</h3>

                                <p className="sub-header">Review and complete your information.</p>
                            </Col>
                        </Row>
                        <Form className="input-form" onSubmit={handleSubmit(onSaveClick)}>
                            <input type="hidden" {...register('intakeId')} />
                            <input type="hidden" {...register('intakePublicId')} />
                            <Row>
                                <Col xs={6}>
                                    <FormInput
                                        tabIndex={1}
                                        label=""
                                        controlId="firstName"
                                        placeholder='First Name'
                                        fieldName="firstName"
                                        control={control}
                                        disabled={store.isReadOnly}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        tabIndex={2}
                                        label=""
                                        control={control}
                                        controlId="lastName"
                                        placeholder='Last Name'
                                        fieldName="lastName"
                                        disabled={store.isReadOnly}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>

                                <Col xs={12} className="mt-2">
                                    <FormInput
                                        tabIndex={3}
                                        label=""
                                        control={control}
                                        controlId="address"
                                        placeholder='Address'
                                        fieldName="address"
                                        disabled={store.isReadOnly}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>

                                <Col xs={12} className="mt-2">
                                    <FormInput
                                        tabIndex={4}
                                        label=""
                                        control={control}
                                        controlId="address2"
                                        placeholder='Address 2'
                                        fieldName="address2"
                                        disabled={store.isReadOnly}
                                        highlightDirty
                                    />
                                </Col>

                                <Col xs={12} className="mt-2">
                                    <FormInput
                                        tabIndex={5}
                                        label=""
                                        control={control}
                                        controlId="city"
                                        placeholder='City'
                                        fieldName="city"
                                        disabled={true}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>

                                <Col xs={12} className="mt-2">
                                    <FormInput
                                        tabIndex={6}
                                        label=""
                                        control={control}
                                        controlId="county"
                                        placeholder='County'
                                        fieldName="county"
                                        disabled={true}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>

                                <Col xs={6} className="mt-2">
                                    <Form.Control
                                        {...register("zipCode")}
                                        type="text"
                                        placeholder="Zip Code"
                                        disabled={store.isReadOnly}
                                        aria-invalid={errors.zipCode ? true : false}
                                        isInvalid={errors.zipCode ? true : false}
                                        maxLength={5}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.zipCode && <span role="alert">{errors.zipCode.message}</span>}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col xs={6} className="mt-2">
                                    {/* <FormSelect
                                        tabIndex={7}
                                        label=""
                                        fieldName="stateId"
                                        control={control}
                                        isRequired={true}
                                        disabled={true}
                                        options={configuration.states}
                                        getOptionLabel={(s) => s.name}
                                        getOptionValue={(s) => s.stateId.toString()}
                                        mapOptionToValue={(opt) => opt?.stateId}
                                        mapValueToOption={(stateId) => _.find(configuration.states, { stateId: stateId })}
                                        onOptionChanged={(stateId) => {
                                            _.find(configuration.states, {
                                                stateId: stateId
                                            })
                                        }}
                                    /> */}

                                    <Form.Select className="form-control"
                                        isInvalid={errors.stateId ? true : false}
                                        aria-label="select state"
                                        {...register('stateId', {
                                            required: "State is required"
                                        })} disabled={true}>
                                        <option value="">Select State</option>
                                        {configuration.states.map(state => <option key={state.abbreviation} value={state.stateId}>{state.name}</option>)}
                                    </Form.Select>
                                </Col>

                                <Col xs={6} className="mt-2">
                                    <FormInput
                                        tabIndex={8}
                                        type="tel"
                                        label=""
                                        placeholder="Phone #"
                                        fieldName="cellPhone"
                                        controlId="cellPhone"
                                        control={control}
                                        disabled={store.isReadOnly}                                        
                                        isRequired
                                    />
                                </Col>

                                <Col xs={6} className="mt-2">
                                    <FormInput
                                        tabIndex={9}
                                        type="tel"
                                        label=""
                                        placeholder="Alternate #"
                                        fieldName="alternatePhone"
                                        controlId="alternatePhone"
                                        control={control}
                                        disabled={store.isReadOnly}
                                    />
                                </Col>

                                <Col xs={12} className="mt-2">
                                    <FormInput
                                        tabIndex={10}
                                        type="email"
                                        label=""
                                        control={control}
                                        controlId="emailAddress"
                                        placeholder='Email'
                                        fieldName="emailAddress"
                                        disabled={store.isReadOnly}
                                        highlightDirty
                                        isRequired
                                    />
                                </Col>

                                <Col xs={12} className="mt-3">
                                    <div className="d-grid gap-2 mb-2">
                                        <Button type="submit" className="text-uppercase flex-fill">
                                            {store.isReadOnly ? 'Continue' : 'Save and Continue'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Page.Content>
                </Page>
            }
        </>
    )
})
